import Loadable from "../../mixins/Loadable";
import MakesApiRequestsMixin from "../../mixins/MakesApiRequestsMixin";
import {isObject} from "../../helpers/object_helpers";
import {isFunction} from "../../helpers/function_helpers";

export default {

    mixins: [Loadable, MakesApiRequestsMixin],

    props: {

        // The url where to send the request or a custom function to execute instead of the default request to the server
        action: {
            type: [String, Function],
            default: null
        },

        // The method to use for the request
        method: {
            type: String,
            default: 'POST'
        },

        // The data to send to the server
        data: {
            type: Object,
            default: () => {
                return {}
            }
        },

        // The lang key of the 'alert' lang file to show an alert message
        alert: {
            type: String,
            default: null
        },

        // The lang key of the 'notification' lang file to show a notification message
        // Can also be 'true' to show a standard notification message
        notification: {
            type: [String, Boolean],
            default: null
        },

        // An object to replace params of the notification or alert message
        langParams: {
            type: Object,
            default: null
        },

        // Whether to prevent any success alert or notification messages after submit
        withoutFeedback: {
            type: Boolean,
            default: false
        },

        // Whether to reload the page after the request was send successfully.
        // Can be useful, if heavy page data updates are necessary.
        reload: {
            type: Boolean,
            default: false
        },

        // An object to define the messages to show to the user within a confirm alert, before the form will be submitted.
        // If not defined, no confirm message will be shown.
        // This object can contain the following key-value-pairs to modify the confirm alert:
        // - 'title': [The title of the confirm message]
        // - 'message': [The body message of the confirm message]
        // - 'inputs': [Optional dynamic input props to ask for additional input]
        // - 'type': [The alert type of the confirm dialog (info, success, warning [DEFAULT], error]
        // - 'accept': [The button text to show on the 'accept' button]
        // - 'cancel': [The button text to show on the 'cancel' button]
        confirm: {
            type: Object | Boolean,
            default: null
        }
    },

    computed: {
        actionUrl() {
            return this.action && !isFunction(this.action) ? this.action : null
        }
    },

    watch: {
        apiRequestLoading(loading) {
            this.isLoading = loading;
        }
    },

    methods: {

        /**
         * Sends the data to the server.
         */
        submit(data = null) {
            this.checkForSubmitPermission((hasPermission, confirmData = {}) => {
                if (hasPermission) {
                    this.isLoading = true;
                    data = data && !(data instanceof MouseEvent) ? data : this.parseFormData({...this.data, ...confirmData});

                    if (!this.action) {
                        // No action required
                        this.isLoading = false;
                        this.handleSuccess({data: data})
                    } else if (this.actionUrl) {
                        // Make a real request to the action url
                        this.sendAjaxRequest(data);
                    } else if (this.action.length < 2) {
                        // Sync function
                        this.action(data);
                        this.isLoading = false;
                        this.handleSuccess({data: data})
                    } else {
                        // Async function
                        this.action(data, (success, response = {data: data}) => {
                            this.isLoading = false;

                            if (success === true) {
                                this.handleSuccess(response)
                            } else if (success === false) {
                                this.handleError(response)
                            }
                        });
                    }

                } else {
                    this.isLoading = false;
                }
            })
        },

        /**
         * Sends the ajax request to the server.
         */
        sendAjaxRequest(data) {
            this.makeApiRequest(this.action, data, this.method).then(() => {
                if (this.reload) {
                    window.location.reload();
                }
            }).catch(() => {
                // Catch existence required to prevent error logging
            })
        },

        /**
         * Checks if the form can be submitted.
         *
         * @param callback
         */
        checkForSubmitPermission(callback) {
            if (this.confirm) {
                this.$confirm({
                    title: isObject(this.confirm) && this.confirm.title ? this.confirm.title : 'Bist du sicher?',
                    text: isObject(this.confirm) && this.confirm.message ? this.confirm.message : 'Möchtest du diese Aktion wirklich durchführen?',
                    type: isObject(this.confirm) && this.confirm.type ? this.confirm.type : 'warning',
                    inputs: isObject(this.confirm) && this.confirm.inputs ? this.confirm.inputs : null,
                    confirmText: isObject(this.confirm) && this.confirm.accept ? this.confirm.accept : null,
                    cancelText: isObject(this.cancel) && this.cancel.accept ? this.cancel.accept : null,
                }).then(({confirmed, data}) => {
                    callback(confirmed, data);
                });
            } else {
                callback(true);
            }
        },

    }
};

